import './AdvancedProgressIndicator.css';

export type ProgressSegment = {
    label: string;
    count: number;
    color: string;
};

type AdvancedProgressIndicatorProps = {
    id: string;
    segments: ProgressSegment[];
    total?: number;
};

const MIN_SEGMENT_PX = 5;

export function AdvancedProgressIndicator({ id, segments, total }: AdvancedProgressIndicatorProps) {
    const computedTotal = total ?? segments.reduce((sum, s) => sum + s.count, 0);
    if (computedTotal === 0) return null;

    const visibleSegments = segments.filter(s => s.count > 0);

    const rawWidths = visibleSegments.map(s => (s.count / computedTotal) * 100);

    // Enforce minimum width: any segment below threshold gets boosted,
    // and excess is subtracted proportionally from the larger segments.
    const minPercent = (MIN_SEGMENT_PX / 200) * 100; // assume ~200px container width baseline
    let adjusted = [...rawWidths];
    let deficit = 0;

    for (let i = 0; i < adjusted.length; i++) {
        if (adjusted[i] < minPercent && adjusted[i] > 0) {
            deficit += minPercent - adjusted[i];
            adjusted[i] = minPercent;
        }
    }

    if (deficit > 0) {
        const largeIndices = adjusted
            .map((w, i) => ({ w, i }))
            .filter(x => x.w > minPercent);
        const largeTotal = largeIndices.reduce((sum, x) => sum + x.w, 0);
        for (const { w, i } of largeIndices) {
            adjusted[i] = w - (deficit * (w / largeTotal));
        }
    }

    return (
        <div className="advanced-progress-bar" id={id}>
            {visibleSegments.map((segment, idx) => {
                const percentage = Math.round((segment.count / computedTotal) * 100);
                return (
                    <div
                        key={segment.label}
                        className="advanced-progress-segment"
                        style={{
                            width: `${adjusted[idx]}%`,
                            backgroundColor: segment.color,
                        }}
                        title={`${segment.label}: ${segment.count} (${percentage}%)`}
                    />
                );
            })}
        </div>
    );
}
